import { useState } from 'react';
import { B } from '../../constants/brand';
import { Modal, ModalFooter, Btn, Icon } from '../ui';
import {
  WIDGET_BY_ID, editableWidgets, reorderWidget, setWidget,
  defaultLayout, layoutsEqual,
} from '../../constants/dashboardWidgets';

// ============================================================
// "Customize dashboard" — reorder / show / hide / resize the widgets.
//
// Edits a LOCAL copy of the layout so Cancel really cancels; Save hands the
// whole ordered list back to the caller, which persists it
// (PUT /api/dashboard/layout).
//
// Reordering works two ways on purpose: drag-and-drop (fast, mouse) and ▲/▼
// buttons (keyboard-accessible — native HTML5 drag has no keyboard equivalent).
//
// Only widgets this viewer can actually load are listed (`editableWidgets`) —
// by role (`adminOnly`) and by named person (`ownerEmails`). An entry they may
// not load stays untouched in their saved layout, so access restored later
// brings the widget back exactly as they left it.
// ============================================================

const WIDTH_LABEL = { full: 'Full width', half: 'Half width' };

export const DashboardCustomizeModal = ({ layout, isAdmin, email, onClose, onSave, saving = false }) => {
  const [draft, setDraft]   = useState(layout);
  const [dragId, setDragId] = useState(null);

  const rows = editableWidgets(draft, { isAdmin, email });
  const dirty = !layoutsEqual(draft, layout);

  // ▲/▼ move relative to the LISTED rows, not to `draft` — a widget this viewer
  // cannot see is still in `draft`, and a plain ±1 there would swap with that
  // invisible entry and look like the button did nothing. So resolve the next
  // listed neighbour and move onto its position.
  const move = (id, delta) => setDraft(d => {
    const listed = editableWidgets(d, { isAdmin, email }).map(e => e.id);
    const neighbour = listed[listed.indexOf(id) + delta];
    return neighbour ? reorderWidget(d, id, neighbour) : d;
  });
  const toggle = (id) => setDraft(d => setWidget(d, id, { visible: !d.find(e => e.id === id)?.visible }));
  const setWidth = (id, width) => setDraft(d => setWidget(d, id, { width }));

  const onDrop = (overId) => {
    if (dragId) setDraft(d => reorderWidget(d, dragId, overId));
    setDragId(null);
  };

  const visibleCount = rows.filter(r => r.visible).length;

  return (
    <Modal title="Customize dashboard" onClose={onClose} width={640}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
        Drag to reorder (or use ▲ ▼), untick to hide, and choose how wide each card sits.
        Your layout is saved to your account, so it follows you to any browser.
      </div>

      <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.map((entry, i) => {
          const meta = WIDGET_BY_ID[entry.id];
          const isDragging = dragId === entry.id;
          return (
            // The whole row is a drop target, but only the grip is draggable —
            // making the row itself draggable fights the checkbox and the width
            // <select> inside it.
            <div key={entry.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(entry.id)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px',
                borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${B.divider}`,
                background: isDragging ? B.accentLt : (entry.visible ? B.surface : B.surfaceMuted),
                opacity: isDragging ? 0.7 : 1,
              }}>
              <span draggable
                onDragStart={() => setDragId(entry.id)}
                onDragEnd={() => setDragId(null)}
                title={`Drag to move ${meta.title}`}
                style={{ color: B.textLt, display: 'flex', paddingTop: 2, cursor: 'grab' }}>
                <Icon name="moreVertical" size={16} />
              </span>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!entry.visible} onChange={() => toggle(entry.id)}
                  style={{ marginTop: 3, cursor: 'pointer' }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: entry.visible ? B.text : B.textMd }}>
                    {meta.title}
                    {meta.adminOnly && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: B.navy, background: B.accentLt, borderRadius: 8, padding: '1px 6px', marginLeft: 8 }}>
                        Admin
                      </span>
                    )}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: B.textMd, marginTop: 2 }}>
                    {meta.description}
                  </span>
                </span>
              </label>

              <select value={entry.width} onChange={(e) => setWidth(entry.id, e.target.value)}
                aria-label={`${meta.title} width`}
                style={{
                  padding: '5px 8px', border: `1px solid ${B.border}`, borderRadius: 8,
                  fontSize: 11.5, color: B.textMd, background: B.surface,
                  fontFamily: 'inherit', cursor: 'pointer',
                }}>
                {Object.entries(WIDTH_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <IconBtn label={`Move ${meta.title} up`}   icon="chevronUp"   disabled={i === 0}
                  onClick={() => move(entry.id, -1)} />
                <IconBtn label={`Move ${meta.title} down`} icon="chevronDown" disabled={i === rows.length - 1}
                  onClick={() => move(entry.id, 1)} />
              </div>
            </div>
          );
        })}
      </div>

      {visibleCount === 0 && (
        <div style={{ fontSize: 12, color: B.amber, marginTop: 10 }}>
          Every widget is hidden — your dashboard will show only the greeting.
        </div>
      )}

      <ModalFooter>
        {/* Staged like any other edit — it still takes a Save, so a mis-click
            is recoverable with Cancel. Pushed left, away from Save. */}
        <Btn variant="ghost" style={{ marginRight: 'auto' }}
          onClick={() => setDraft(defaultLayout())}>Reset to default</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(draft)} disabled={!dirty} loading={saving}>Save layout</Btn>
      </ModalFooter>
    </Modal>
  );
};

const IconBtn = ({ label, icon, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label} title={label}
    style={{
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
      color: B.textMd, display: 'flex', padding: 2, lineHeight: 1,
    }}>
    <Icon name={icon} size={13} />
  </button>
);

export default DashboardCustomizeModal;
